import { MouseEventHandler } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  IconButton,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';

import { trackContentCardEdit } from '../../../models/content/analytics';
import { trackEditClick } from '../../../models/content-submission/analytics';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { contentSelectors } from '../../../models/content';

interface IContentActionEdit {
  showText?: boolean;
  onClick: MouseEventHandler | undefined;
  contentTitle: string;
  wideMode?: boolean;
  container?: HTMLElement | null;
}

const ContentActionEdit = ({
  showText = false,
  onClick,
  contentTitle,
  wideMode = false,
  container,
}: IContentActionEdit) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  const displayText = showText || wideMode;

  const actionLabel = t('content.edit_action', { title: contentTitle });

  return displayText ? (
    <Button
      variant="text"
      leftIcon={<Icon>edit</Icon>}
      onClick={onClick}
      aria-label={actionLabel}
      label={t('common.edit')}
    />
  ) : (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <IconButton
          aria-label={actionLabel}
          iconName="edit"
          onClick={onClick}
        />
      </TooltipTrigger>
      <TooltipContent description={t('common.edit')} container={container} />
    </TooltipRoot>
  );
};

type ContentActionEditWrapperProps = {
  contentId: number;
  analyticsData: IAnalyticsData;
  showText?: boolean;
  wideMode?: boolean;
  container?: HTMLElement | null;
};

const ContentActionEditWrapper = ({
  contentId,
  analyticsData,
  showText,
  wideMode,
  container,
}: ContentActionEditWrapperProps) => {
  const history = useHistory();

  const handleClick = () => {
    const editRoute = `/contents/${contentId}/edit`;
    history.push(editRoute);

    trackContentCardEdit(contentId, analyticsData);
    trackEditClick({ content_id: contentId });
  };

  const contentTitle = usePatronSelector((s) =>
    contentSelectors.getContentFunctionalTitle(s, { contentId })
  );

  return (
    <ContentActionEdit
      onClick={handleClick}
      contentTitle={contentTitle}
      showText={showText}
      wideMode={wideMode}
      container={container}
    />
  );
};

export { ContentActionEdit };

export default ContentActionEditWrapper;
